import { CDNCharactersAddress, webchatTokenRetrievalAddress, defaultDirectLineAddress } from '../models/constants'
import { RetorikLanguageData, RetorikMainComponentConfiguration, RetorikMainComponentProps, RetorikOtherData } from '../models/retorikTypes'
import type { WidgetConfiguration } from '../store/models/types'

interface FetchedIntegrationMode {
  integrationMode: string
  widgetConfiguration: WidgetConfiguration | undefined
}

const getRetorikConfigs = async (props: RetorikMainComponentProps, baseAddress: string): Promise<(RetorikMainComponentConfiguration & RetorikLanguageData & RetorikOtherData) | null> => {
  const retorikProps = { ...props }
  let res: (RetorikMainComponentConfiguration & RetorikLanguageData & RetorikOtherData) | null = null
  const fullAddress = `${baseAddress}api/integration/framework`

  try {
    const response = await fetch(fullAddress)
    const json = await response.json()
    res = processJson(json, retorikProps, baseAddress)
  } catch (error) {
    console.log('Retorik Framework > error when fetching integration endpoint : ', error)
  }

  return res
}

const processJsonToGetWidgetConfiguration = async (json): Promise<WidgetConfiguration | undefined> => {
  const widgetData = json.widgetButton

  if (widgetData) {
    let buttonImageUrl: string | undefined = undefined

    // If needed, get an image to display in the button : manual config > fetched config > fetched character
    if (!widgetData.hideButtonThumbnail) {
      if (json.buttonThumbnail) {
        buttonImageUrl = json.buttonThumbnail
      } else if (json.personnage) {
        try {
          const url = json.personnage.indexOf('http') === -1 ? `${CDNCharactersAddress}/${json.personnage}` : json.personnage
          const characterPortraitFilename = await fetch(`${url}${url.endsWith('/') ? '' : '/'}manifest.json?v=${Date.now()}`)
            .then((response) => response.json())
            .then((json) => json.portrait)
          characterPortraitFilename && (buttonImageUrl = `${url}${url.endsWith('/') ? '' : '/'}${characterPortraitFilename}`)
        } catch (error) {
          console.log('Retorik Framework > error while fetching character data : ', error)
        }
      }
    }

    // Calculate the top / left parts of the button and the frame depending on the given position
    let buttonTop: string | undefined = undefined
    let frameTop: string | undefined = undefined
    let buttonLeft: string | undefined = undefined
    let frameLeft: string | undefined = undefined

    // Top part
    if (['top-right', 'top-left'].includes(widgetData.position)) {
      buttonTop = `${widgetData.offsetY}px`
      frameTop = `${widgetData.offsetY}px`
    } else if (['center-left', 'center-right'].includes(widgetData.position)) {
      buttonTop = `${window.innerHeight / 2 - 20 + widgetData.offsetY}px`
      // Get the top position of the frame : half the window's height minus half the default widget's size (630px) plus the offseet
      frameTop = `${window.innerHeight / 2 - 315 + widgetData.offsetY}px`
    }

    // Left part
    if (['top-left', 'bottom-left', 'center-left'].includes(widgetData.position)) {
      buttonLeft = `${widgetData.offsetX}px`
      frameLeft = `${widgetData.offsetX}px`
    } else if (widgetData.position === 'bottom-center') {
      // Max button width = 200px, we substract half this amount
      buttonLeft = `${window.innerWidth / 2 - 100 + widgetData.offsetX}px`
      // Get the left position of the frame : half the window's width minus half the default widget's width (400px) plus the offset
      frameLeft = `${window.innerWidth / 2 - 200 + widgetData.offsetX}px`
    }

    // Get the other positions depending on the given position
    const bottom = ['bottom-right', 'bottom-left', 'bottom-center'].includes(widgetData.position) ? `${widgetData.offsetY}px` : undefined
    const right = ['top-right', 'bottom-right', 'center-right'].includes(widgetData.position) ? `${widgetData.offsetX}px` : buttonLeft === undefined ? '0px' : undefined

    const widgetConfiguration: WidgetConfiguration = {
      width: 400, // not available in fetched configuration : base = 400px
      button: {
        display: true,
        text: {
          content: widgetData.text,
          color: widgetData.colors?.default?.text,
          colorHover: widgetData.colors?.hover?.text,
          bold: false, // not available in fetched configuration : base = false
          maxWidth: 200 // not available in fetched configuration : base = 200px
        },
        image: {
          hide: widgetData.hideButtonThumbnail,
          url: buttonImageUrl,
          position: 'left', // not available in fetched configuration : base = 'left'
          height: undefined, // not available in fetched configuration : base = undefined
          marginY: true // not available in fetched configuration : base = true
        },
        border: {
          color: widgetData.colors?.default?.border,
          colorHover: widgetData.colors?.hover?.border,
          thickness: widgetData.borderThickness,
          rounded: widgetData.roundedEdges
        },
        background: {
          color: widgetData.colors?.default?.background,
          colorHover: widgetData.colors?.hover?.background
        },
        position: {
          top: buttonTop,
          bottom: bottom,
          left: buttonLeft,
          right: right
        }
      },
      large: {
        width: undefined,
        height: '630px',
        border: {
          color: undefined,
          rounded: undefined,
          thickness: undefined
        },
        position: {
          top: frameTop,
          bottom: bottom,
          left: frameLeft,
          right: right
        }
      },
      thumbnail: undefined
    }

    return widgetConfiguration
  }

  return undefined
}

const getIntegrationModeAndWidgetConfigurationIfNeeded = async (baseAddress: string, locale: string): Promise<FetchedIntegrationMode> => {
  const dataToReturn: FetchedIntegrationMode = {
    integrationMode: 'fullscreen',
    widgetConfiguration: undefined
  }

  try {
    const response = await fetch(`${baseAddress}api/integration/framework?culture=${locale}`)
    const json = await response.json()
    const fetchedIntegrationMode = json.integrationMode

    if (fetchedIntegrationMode === 'widget') {
      dataToReturn.integrationMode = 'widget'
      dataToReturn.widgetConfiguration = await processJsonToGetWidgetConfiguration(json)
    }
  } catch (error) {
    console.log('Retorik Framework > error when fetching integration endpoint : ', error)
  }

  return dataToReturn
}

const getWidgetConfiguration = async (baseAddress: string, locale: string): Promise<WidgetConfiguration | undefined> => {
  try {
    const response = await fetch(`${baseAddress}api/integration/framework?culture=${locale}`)
    const json = await response.json()

    return await processJsonToGetWidgetConfiguration(json)
  } catch (error) {
    console.log('Retorik Framework > error when fetching integration endpoint : ', error)
    return undefined
  }
}

const getAddressWithoutTenant = (address: string, tenant: string | undefined): string => {
  if (tenant && address.indexOf(`/${tenant}`)) {
    address = address.replace(`/${tenant}`, '')
  }

  if (address.endsWith('/')) {
    return address.slice(0, -1)
  }

  return address
}

const processJson = (json: any, retorikProps: RetorikMainComponentProps, baseAddress: string): (RetorikMainComponentConfiguration & RetorikLanguageData & RetorikOtherData) | null => {
  if (json) {
    // Get full address for backgrounds & logo
    let logo, background, newsBackground, personnage
    if (json.personnage) {
      if (json.personnage.indexOf('http') === -1) {
        personnage = `${CDNCharactersAddress}/${json.personnage}`
      } else {
        personnage = json.personnage
      }
    } else {
      personnage = `${CDNCharactersAddress}/long-shot/zahra`
    }

    const addressWithoutTenant = getAddressWithoutTenant(baseAddress, retorikProps.addressData.tenant)

    if (json.logo) {
      logo = `${addressWithoutTenant}${json.logo}`
    }
    if (json.background?.length) {
      background = `${addressWithoutTenant}${json.background}`
    } else {
      background = new URL('../assets/images/background.webp', import.meta.url)
    }
    if (json.newsBackground?.length) {
      newsBackground = `${addressWithoutTenant}${json.newsBackground}`
    } else {
      newsBackground = new URL('../assets/images/background.webp', import.meta.url)
    }

    // Handle size & height : string or number
    let size = json.agentSize || undefined
    if (json.agentSize && !isNaN(+json.agentSize)) {
      size = +json.agentSize // convert to int
    }
    let height = json.agentHeight || undefined
    if (json.agentHeight && !isNaN(+json.agentHeight)) {
      height = +json.agentHeight // convert to int
    }

    // For widget
    // Handle size & height : string or number
    let sizeWidget = json.agentSizeWidget || json.agentSize || undefined
    if (json.agentSizeWidget && !isNaN(+json.agentSizeWidget)) {
      sizeWidget = +json.agentSizeWidget // convert to int
    }
    let heightWidget = json.agentHeightWidget || json.agentHeight || undefined
    if (json.agentHeightWidget && !isNaN(+json.agentHeightWidget)) {
      heightWidget = +json.agentHeightWidget // convert to int
    }

    // Process token endpoint from manual configuration and incoming data
    let tokenEndpoint = ''
    if (retorikProps.addressData?.directline?.tokenEndpoint) {
      tokenEndpoint = retorikProps.addressData.directline.tokenEndpoint
    } else if (retorikProps.addressData?.baseURI?.includes(webchatTokenRetrievalAddress)) {
      tokenEndpoint = retorikProps.addressData.baseURI
    } else if (json.directLine_tokenEndpoint) {
      tokenEndpoint = json.directLine_tokenEndpoint
    } else {
      tokenEndpoint = `${baseAddress}${webchatTokenRetrievalAddress}`
    }

    // Prevent passive speech recognition if no name was given for the character.
    // Used for retrocompatibility since v2.0.4
    if (!json.characterName && json.speechRecognitionOptions) {
      ;(json.speechRecognitionOptions.enableContinuousRecognitionOnAllDevices = false), (json.speechRecognitionOptions.enableContinuousRecognitionOn = undefined)
    }

    const props: RetorikMainComponentConfiguration & RetorikLanguageData & RetorikOtherData = {
      config: {
        fullSize: undefined,
        isUsedOnBorne: undefined,
        hideParametersButtonInMenu: json.hideParametersButtonInMenu,
        locales: {
          getDefaultFromBrowser: undefined
        },
        position: {
          searchForLocation: json.detectLocation,
          latitude: undefined,
          longitude: undefined,
          city: undefined,
          country: undefined
        },
        subtitles: json.subtitles,
        subtitlesPosition: json.subtitlesPosition,
        logo: json.logo ? { src: logo } : undefined,
        answerpanel: undefined,
        skipWelcome: json.skipWelcome,
        preventExpectedInputHint: json.preventExpectedInputHint,
        pagination: undefined,
        remote: undefined,
        languageChoiceBottom: undefined,
        companyName: json.companyName,
        companyNameLocalized: undefined,
        subCompanyMessage: json.subCompanyMessage,
        subCompanyMessageLocalized: undefined,
        loaderInformationTexts: {
          text: {
            top: json.textModeHomeTitle,
            bottom: json.textModeHomeSubtitle
          },
          vocal: {
            top: json.voiceModeHomeTitle,
            bottom: json.voiceModeHomeSubtitle
          }
        },
        loaderInformationTextsLocalized: {
          text: {
            top: json.textModeHomeTitleLocalized,
            bottom: json.textModeHomeSubtitleLocalized
          },
          vocal: {
            top: json.voiceModeHomeTitleLocalized,
            bottom: json.voiceModeHomeSubtitleLocalized
          }
        },
        hideRetorikLogo: json.hideRetorikLogo,
        baseMenu: undefined,
        customMenu: undefined,
        subMenu: undefined,
        menu: json.menu,
        hideMenu: json.hideMenu,
        hideControls: json.hideControls,
        doNotDetectDeviceFromUserAgent: json.doNotDetectDeviceFromUserAgent,
        enableConversationCookie: json.enableConversationCookie,
        conversationCookieMaxAge: json.conversationCookieMaxAge,
        enableTelemetry: json.enableTelemetry,
        allowSwitchLayout: json.allowSwitchLayout,
        forceMobileView: json.forceMobileFullScreenLayout,
        enableSpeechCaching: json.enableSpeechCaching,
        speechRecognitionOptions: {
          timerBeforeSpeechEnd: json.speechRecognitionOptions?.timerBeforeSpeechEnd,
          enableActivationSound: json.speechRecognitionOptions?.enableActivationSound,
          enableSendMessageOnMicrophoneClose: json.speechRecognitionOptions?.enableSendMessageOnMicrophoneClose,
          enableContinuousRecognitionOnAllDevices: json.speechRecognitionOptions?.enableContinuousRecognitionOnAllDevices,
          enableContinuousRecognitionOn: json.speechRecognitionOptions?.enableContinuousRecognitionOn,
          wakeWords: json.speechRecognitionOptions?.wakeWords,
          grammars: json.speechRecognitionOptions?.grammars,
          inputHintBehaviour: json.speechRecognitionOptions?.inputHintBehaviour
        },
        speechSynthesisOptions: json.speechSynthesisOptions,
        enableDocumentPrinting: undefined,
        disableSpeechMode: json.disableSpeechMode,
        disableTextMode: json.disableTextMode,
        disableSound: json.disableSound,
        disableHistory: json.disableHistory,
        disableSubtitles: json.disableSubtitles,
        timerForFilterSelectionListMode: undefined,
        // Pattern != null means not null nor undefined
        useOldRemote: json.useOldRemote != null ? json.useOldRemote : true,
        preventVirtualKeyboard: undefined,
        channelData: json.channelData
      },
      viewsConfig: {
        homeRoute: 'home',
        webcamRotation: undefined,
        views: {
          home: {
            background: {
              style: 'image',
              image: background,
              video: undefined,
              webcam: undefined
            }
          },
          news: {
            intervalInSeconds: json.newsConfig?.intervalInSeconds || 2,
            loop: true,
            openingVideo: json.newsConfig?.openingVideo,
            endingVideo: json.newsConfig?.endingVideo,
            background: {
              style: 'image',
              image: newsBackground,
              video: undefined,
              webcam: undefined
            },
            showMenu: undefined,
            bannerTitle: json.newsConfig?.bannerTitle,
            bannerTitleLocalized: undefined,
            removeTitleFromTextToSpeak: json.newsConfig?.removeTitleFromTextToSpeak,
            removeSubtitleFromTextToSpeak: json.newsConfig?.removeSubtitleFromTextToSpeak,
            bannerPosition: json.newsConfig?.bannerPosition,
            mediaPosition: json.newsConfig?.mediaPosition
          }
        }
      },
      veilleConfig: {
        enabled: json.veilleConfig?.enabled,
        delayBeforeLaunchInSeconds: json.veilleConfig?.delayBeforeLaunchInSeconds,
        killConversationOnLaunch: json.veilleConfig?.killConversationOnLaunch,
        autoExitFromWebcam: json.veilleConfig?.autoExitFromWebcam,
        views: {
          news: {
            enabled: json.veilleConfig?.views?.news?.enabled,
            muted: json.veilleConfig?.views?.news?.muted
          },
          screenSaver: {
            enabled: json.veilleConfig?.views?.screenSaver?.enabled,
            url: json.veilleConfig?.views?.screenSaver?.url,
            timerBeforeSwitchInSeconds: json.veilleConfig?.views?.screenSaver?.timerBeforeSwitchInSeconds
          },
          slideShow: {
            enabled: json.veilleConfig?.views?.slideShow?.enabled,
            urls: json.veilleConfig?.views?.slideShow?.urls,
            timerBeforeSwitchInSeconds: json.veilleConfig?.views?.slideShow?.timerBeforeSwitchInSeconds
          }
        }
      },
      mapConfig: {
        mapCenter: json.mapConfig?.mapCenter,
        limitAroundCenter: json.mapConfig?.limitAroundCenter,
        settings: {
          categories: json.mapConfig?.settings?.categories,
          hospitalityCategories: json.mapConfig?.settings?.hospitalityCategories
            ? {
                afaire: {
                  display: !!json.mapConfig?.settings?.hospitalityCategories?.afaire?.display,
                  color: json.mapConfig?.settings?.hospitalityCategories?.afaire?.color,
                  label: json.mapConfig?.settings?.hospitalityCategories?.afaire?.label,
                  localizedLabel: undefined
                },
                avoir: {
                  display: !!json.mapConfig?.settings?.hospitalityCategories?.avoir?.display,
                  color: json.mapConfig?.settings?.hospitalityCategories?.avoir?.color,
                  label: json.mapConfig?.settings?.hospitalityCategories?.avoir?.label,
                  localizedLabel: undefined
                },
                hebergement: {
                  display: !!json.mapConfig?.settings?.hospitalityCategories?.hebergement?.display,
                  color: json.mapConfig?.settings?.hospitalityCategories?.hebergement?.color,
                  label: json.mapConfig?.settings?.hospitalityCategories?.hebergement?.label,
                  localizedLabel: undefined
                },
                produit: {
                  display: !!json.mapConfig?.settings?.hospitalityCategories?.produit?.display,
                  color: json.mapConfig?.settings?.hospitalityCategories?.produit?.color,
                  label: json.mapConfig?.settings?.hospitalityCategories?.produit?.label,
                  localizedLabel: undefined
                },
                restaurant: {
                  display: !!json.mapConfig?.settings?.hospitalityCategories?.restaurant?.display,
                  color: json.mapConfig?.settings?.hospitalityCategories?.restaurant?.color,
                  label: json.mapConfig?.settings?.hospitalityCategories?.restaurant?.label,
                  localizedLabel: undefined
                },
                service: {
                  display: !!json.mapConfig?.settings?.hospitalityCategories?.service?.display,
                  color: json.mapConfig?.settings?.hospitalityCategories?.service?.color,
                  label: json.mapConfig?.settings?.hospitalityCategories?.service?.label,
                  localizedLabel: undefined
                }
              }
            : undefined,
          showFilter: json.mapConfig?.settings?.showFilter
        }
      },
      chatbotData: {
        size: size,
        height: height
      },
      chatbotDataWidget: {
        size: sizeWidget,
        height: heightWidget
      },
      agentData: {
        url: personnage,
        name: json.characterName,
        animationsUrl: json.animationsUrl || undefined,
        portrait: json.widgetButton?.agentThumbnail || undefined
      },
      ponyfillFactoryCredentials: {
        region: json.voiceCredentialsRegion,
        subscriptionKey: json.voiceCredentialsSubscriptionKey,
        speechRecognitionHostnamesRecord: json.speechRecognizerHosts,
        speechRecognitionHostname: undefined,
        speechSynthesisHostname: json.speechSynthesizerHost
      },
      addressData: {
        ...retorikProps.addressData,
        directline: {
          tokenEndpoint: tokenEndpoint,
          address: retorikProps.addressData?.directline?.address || json.directLine_address || defaultDirectLineAddress
        }
      },
      userData: retorikProps.userData,
      customVoice: {
        voice: json.voice,
        gender: undefined
      },
      colors: {
        theme: json.colorSettings.theme,
        primary: json.colorSettings.primary,
        secondary: json.colorSettings.secondary,
        black: json.colorSettings.black,
        whereToEatColor: json.colorSettings.whereToEatColor,
        whereToSleepColor: json.colorSettings.whereToSleepColor,
        tobeSeenColor: json.colorSettings.tobeSeenColor,
        tobeDoneColor: json.colorSettings.tobeDoneColor,
        localProductsColor: json.colorSettings.localProductsColor,
        servicesColor: json.colorSettings.servicesColor,
        companyName: json.colorSettings.companyName,
        agendaColor: json.colorSettings.agendaColor,
        occupantColor: json.colorSettings.occupantColor,
        loader: {
          animation: json.colorSettings.loader?.animation,
          background: json.colorSettings.loader?.background,
          text: json.colorSettings.loader?.text,
          toggle: {
            text: json.colorSettings.loader?.toggle?.text,
            background: json.colorSettings.loader?.toggle?.background
          },
          button: {
            background: {
              default: json.colorSettings.loader?.button?.background?.default,
              hover: json.colorSettings.loader?.button?.background?.hover
            },
            border: {
              default: json.colorSettings.loader?.button?.border?.default,
              hover: json.colorSettings.loader?.button?.border?.hover
            },
            text: {
              default: json.colorSettings.loader?.button?.text?.default,
              hover: json.colorSettings.loader?.button?.text?.hover
            }
          }
        },
        card: {
          frame: {
            background: json.colorSettings.card?.frame?.background,
            border: json.colorSettings.card?.frame?.border,
            text: json.colorSettings.card?.frame?.text
          },
          button: {
            background: {
              default: json.colorSettings.card?.button?.background?.default,
              hover: json.colorSettings.card?.button?.background?.hover
            },
            border: {
              default: json.colorSettings.card?.button?.border?.default,
              hover: json.colorSettings.card?.button?.border?.hover
            },
            text: {
              default: json.colorSettings.card?.button?.text?.default,
              hover: json.colorSettings.card?.button?.text?.hover
            }
          },
          discoverButton: {
            background: {
              default: json.colorSettings.card?.discoverButton?.background?.default,
              hover: json.colorSettings.card?.discoverButton?.background?.hover
            },
            border: {
              default: json.colorSettings.card?.discoverButton?.border?.default,
              hover: json.colorSettings.card?.discoverButton?.border?.hover
            },
            text: {
              default: json.colorSettings.card?.discoverButton?.text?.default,
              hover: json.colorSettings.card?.discoverButton?.text?.hover
            }
          }
        },
        textMode: {
          panel: {
            background: json.colorSettings.textMode?.panel?.background,
            border: json.colorSettings.textMode?.panel?.border,
            conversationUser: json.colorSettings.textMode?.panel?.conversationUser,
            conversationBot: json.colorSettings.textMode?.panel?.conversationBot,
            backgroundUser: json.colorSettings.textMode?.panel?.backgroundUser,
            backgroundBot: json.colorSettings.textMode?.panel?.backgroundBot
          }
        },
        vocalMode: {
          subtitles: {
            text: json.colorSettings.vocalMode?.subtitles?.text,
            background: json.colorSettings.vocalMode?.subtitles?.background
          }
        },
        formInput: {
          text: {
            default: json.colorSettings.formInput?.inputText?.default,
            hover: json.colorSettings.formInput?.inputText?.hover
          },
          inputRadioCheckbox: {
            unchecked: {
              background: json.colorSettings.formInput?.inputRadioCheckbox?.unchecked?.background,
              border: json.colorSettings.formInput?.inputRadioCheckbox?.unchecked?.border
            },
            checked: {
              background: json.colorSettings.formInput?.inputRadioCheckbox?.checked?.background,
              border: json.colorSettings.formInput?.inputRadioCheckbox?.checked?.border,
              item: json.colorSettings.formInput?.inputRadioCheckbox?.checked?.item
            }
          },
          inputButton: {
            background: {
              default: json.colorSettings.formInput?.inputButton?.background?.default,
              hover: json.colorSettings.formInput?.inputButton?.background?.hover
            },
            border: {
              default: json.colorSettings.formInput?.inputButton?.border?.default,
              hover: json.colorSettings.formInput?.inputButton?.border?.hover
            },
            text: {
              default: json.colorSettings.formInput?.inputButton?.text?.default,
              hover: json.colorSettings.formInput?.inputButton?.text?.hover
            }
          },
          likert: {
            default: json.colorSettings.formInput?.likert?.default,
            hover: json.colorSettings.formInput?.likert?.hover
          }
        }
      },
      defaultMode: json.defaultMode,
      skipLoader: json.skipLoader,
      // Language data
      defaultCulture: json.defaultCulture,
      supportedCultures: json.supportedCultures,
      // Other data
      customScripts: json.customScripts,
      customCSS: json.customCSS,
      features: json.features
    }

    return props
  }

  return null
}

export default getRetorikConfigs
export { getIntegrationModeAndWidgetConfigurationIfNeeded, getWidgetConfiguration }
